import { Inject, Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { catchError, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import * as ObjectActions from './user-management.actions';
import * as AppActions from '../app/actions';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { GeneralApiResponseInterface } from '../../layout/main/main.model';
import { UserInterface } from './user-management.model';
import { IotDeviceManagementPortalAppState } from '../iotDeviceManagementPortalApp.reducer';
import { Store } from '@ngrx/store';
import { PageFiltersAndSettingsService } from '../../shared/service/page-filters-and-settings/page-filters-and-settings.service';

@Injectable()
export class UserManagementEffects {
  constructor(
    private actions$: Actions<ObjectActions.UserManagementActions>,
    private http: HttpClient,
    private readonly store: Store<IotDeviceManagementPortalAppState>,
    private readonly pageFiltersAndSettings: PageFiltersAndSettingsService,
    @Inject('API_BASE_URL') private readonly baseUrl: string,
  ) {}

  private USER_URLS = {
    GET: {
      USER: '/user',
    },
    POST: {
      USER: '/user',
    },
    PATCH: {
      USER: '/user',
    },
    DELETE: {
      USER: '/user',
      USER_BULK: '/user/bulk/delete',
    },
  };

  @Effect()
  getUsers$ = this.actions$.pipe(
    ofType(ObjectActions.UserManagementActionTypes.GetUsers),
    switchMap((objectData: ObjectActions.GetUsers) => {
      this.store.dispatch(new AppActions.ShowLoader());
      const url = `${this.baseUrl}${this.USER_URLS.GET.USER}`;
      let params: HttpParams = new HttpParams();

      params = params
        .append('page', String(objectData.query.page))
        .append('limit', String(objectData.query.pageSize || 1000));

      if (objectData.query.order) {
        params = params
          .set('order', `${objectData.query.order}`)
          .set('orderType', `${objectData.query.orderType.toUpperCase()}`);
      }

      if (objectData.query.search !== undefined) {
        params = params.set('searchType', String('user_search'));
        params = params.set('search', String(objectData.query.search));
      }

      if (objectData.query.startDate) {
        params = params.set('startDate', `${objectData.query.startDate}`).set('endDate', `${objectData.query.endDate}`);
      }

      return this.http
        .get<GeneralApiResponseInterface<UserInterface[]>>(url, {
          params,
        })
        .pipe(
          switchMap((response: GeneralApiResponseInterface<UserInterface[]>) => {
            return of(new ObjectActions.GetUsersCompleted(response['data'], response['total']), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
    }),
  );


  @Effect()
  createUser$ = this.actions$.pipe(
    ofType(ObjectActions.UserManagementActionTypes.CreateUser),
    switchMap((objectData: ObjectActions.CreateUser) => {
      return this.http
        .post<GeneralApiResponseInterface<UserInterface[]>>(`${this.baseUrl}${this.USER_URLS.POST.USER}`, {
          ...objectData.payload,
        })
        .pipe(
          switchMap((response) => {
            return of(new ObjectActions.CreateUserCompleted(response), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
    }),
  );

  @Effect()
  getUser$ = this.actions$.pipe(
    ofType(ObjectActions.UserManagementActionTypes.GetUser),
    switchMap((device: ObjectActions.GetUser) => {
      this.store.dispatch(new AppActions.ShowLoader());
      const url = `${this.baseUrl}${this.USER_URLS.GET.USER}/${device.deviceId}`;
      return this.http.get<GeneralApiResponseInterface<UserInterface>>(url).pipe(
        switchMap((response) => {
          return of(new ObjectActions.GetUserCompleted(response), new AppActions.HideLoader());
        }),
        catchError((errorRes) => {
          return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
        }),
      );
    }),
  );

  @Effect()
  updateDevice$ = this.actions$.pipe(
    ofType(ObjectActions.UserManagementActionTypes.UpdateUser),
    switchMap((objectData: ObjectActions.UpdateUser) => {
      this.store.dispatch(new AppActions.ShowLoader());
      const url = `${this.baseUrl}${this.USER_URLS.PATCH.USER}/${objectData.id}`;
      return this.http
        .patch<GeneralApiResponseInterface<UserInterface>>(url, {
          ...objectData.payload,
        })
        .pipe(
          switchMap((response) => {
            return of(new ObjectActions.UpdateUserCompleted(response), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
    }),
  );

  @Effect()
  deleteUser = this.actions$.pipe(
    ofType(ObjectActions.UserManagementActionTypes.DeleteUser),
    switchMap((objectData: ObjectActions.DeleteUser) => {
      this.store.dispatch(new AppActions.ShowLoader());
      let url = `${this.baseUrl}${this.USER_URLS.DELETE.USER}`;

      if (Array.isArray(objectData.payload) && objectData.payload.length > 1) {
        const httpOptions = {
          headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
          body: {
            users: objectData.payload,
          },
        };
        url = `${this.baseUrl}${this.USER_URLS.DELETE.USER_BULK}`;
        return this.http.delete<GeneralApiResponseInterface<UserInterface[]>>(url, httpOptions).pipe(
          switchMap((response) => {
            return of(new ObjectActions.DeleteUserCompleted(response['data']), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
      }
      return this.http.delete<GeneralApiResponseInterface<UserInterface[]>>(`${url}/${objectData.payload[0]}`).pipe(
        switchMap((response) => {
          return of(new ObjectActions.DeleteUserCompleted(response['data']), new AppActions.HideLoader());
        }),
        catchError((errorRes) => {
          return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
        }),
      );
    }),
  );
}
